import type {
  IPreApprovalClient,
  IPreApprovalClientCity,
  IPreApprovalCurrentStatus,
  TPreApprovalServerStep
} from '../api.types/pre-approval.api-types';

/** Клиент в ответе `current`: номер приходит без `+` (`77011234567`). */
export interface IPreApprovalClientDto {
  uuid: string;
  phone_confirmed_date: string;
  auth_otp_create_date: string;
  create_date: string;
  client_iin: string;
  phone_number: string;
  // TODO(pre-approval-client-compat): сделать обязательным, когда не останется процессов, начатых до появления `cityId` в precheck
  city?: IPreApprovalClientCity | null;
}

/** Ответ `preapproval/current`: шаг приходит строкой, остальные поля — только когда есть. */
export interface IPreApprovalCurrentStatusDto {
  // TODO(pre-approval-client-compat): сделать обязательным, когда не останется процессов, начатых до появления `client` в `current`
  client?: IPreApprovalClientDto | null;
  step: string;
  biometricSessionUuid?: string | null;
  limitResultId?: string | null;
  otpExpiresAt?: string | null;
  otpResendAvailableAt?: string | null;
}

const SERVER_STEPS: readonly string[] = [
  'otp',
  'biometry',
  'waiting-biometry',
  'biometry-timeout',
  'biometry-failed',
  'biometry-stale',
  'waiting-limit',
  'result-success',
  'result-declined',
  'result-error'
] satisfies TPreApprovalServerStep[];

const isServerStep = (step: string): step is TPreApprovalServerStep =>
  SERVER_STEPS.includes(step);

/** Номер в виде `+7XXXXXXXXXX`, как его отправляет фронт: бэкенд отдаёт только цифры. */
const toPhoneNumber = (raw: string): string => `+${raw.replace(/\D/g, '')}`;

/** Клиент из ответа `current`: номер — к виду фронта, отсутствующий город — `null`. */
export const toClient = (dto: IPreApprovalClientDto): IPreApprovalClient => ({
  uuid: dto.uuid,
  phone_confirmed_date: dto.phone_confirmed_date,
  auth_otp_create_date: dto.auth_otp_create_date,
  create_date: dto.create_date,
  client_iin: dto.client_iin,
  phone_number: toPhoneNumber(dto.phone_number),
  city: dto.city ?? null
});

/**
 * Приводит ответ `current` к доменному типу.
 * Неизвестный шаг → `null`: процесс считается не начатым, и клиента ведут на согласие,
 * а не роняют раздел из-за нового значения на бэкенде.
 */
export const toCurrentStatus = (
  dto: IPreApprovalCurrentStatusDto | null
): IPreApprovalCurrentStatus | null => {
  if (!dto) return null;
  if (!isServerStep(dto.step)) {
    console.warn(`[pre-approval] unknown current step: ${dto.step}`);
    return null;
  }
  return {
    client: dto.client ? toClient(dto.client) : null,
    step: dto.step,
    biometricSessionUuid: dto.biometricSessionUuid || null,
    limitResultId: dto.limitResultId || null,
    otpExpiresAt: dto.otpExpiresAt || null,
    otpResendAvailableAt: dto.otpResendAvailableAt || null
  };
};
